import { useCallback, useMemo } from 'react';

import { GridContainer } from '../../grid-container/grid-container';
import { MappedComponentPropertiesWithCqItems } from '../../../model/mapped-component.model';
import { useHasGradientBackground, useHasMidBlueBackground } from '../../../utils/use-has-background-class';
import { getComponentFromProps, getPropsFromModel } from '../../../utils/use-child-components';

interface UseCasesGridProps extends Omit<MappedComponentPropertiesWithCqItems, 'isInEditor'> {}

export const UseCasesGrid = ({ appliedCssClassNames, cqItems, cqPath, cqItemsOrder }: UseCasesGridProps) => {
  const hasGradientBackground = useHasGradientBackground(appliedCssClassNames);
  const hasMidBlueBackground = useHasMidBlueBackground(appliedCssClassNames);

  const gridItems = useMemo(() => Object.entries(cqItems || /* istanbul ignore next */ {}), [cqItems]);

  const desktopColumnNumber = useMemo(() => {
    if (gridItems.length > 3) {
      return 3;
    }
    if (gridItems?.length === 3) {
      return 4;
    }
    return 6;
  }, [gridItems]);

  const elementPropList = useMemo(
    () =>
      cqItemsOrder.map((key) => {
        const item = cqItems[key];
        const contentBlockProps = getPropsFromModel(item, cqPath, key);
        const UseCasesContentBlock = getComponentFromProps(contentBlockProps);
        return { key, elementProps: { ...contentBlockProps, UseCasesContentBlock } };
      }),
    [cqItems, cqItemsOrder, cqPath]
  );

  const getContentBlockGridProps = useCallback(
    () => ({
      mobile: 12,
      tablet: 6,
      smallDesktop: desktopColumnNumber,
      desktop: desktopColumnNumber,
      testId: 'snowflake-use-cases-grid-item',
    }),
    [desktopColumnNumber]
  );

  return (
    <GridContainer
      gridContainerClass={appliedCssClassNames}
      elementPropList={elementPropList}
      getGridProps={getContentBlockGridProps}
      renderItem={(
        { elementProps: { UseCasesContentBlock, ...contentBlockProps } },
        addSameHeightRef,
        wrapperMinHeights,
        index
      ) => (
        <div data-testid="snowflake-use-cases-content-block" className="snowflake-use-cases-row-content-block-grid">
          <UseCasesContentBlock
            titleRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'title');
            }}
            textRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'text');
            }}
            ctaRef={(el: HTMLElement) => {
              addSameHeightRef(el, 'cta');
            }}
            titleMinHeight={wrapperMinHeights.title}
            textMinHeight={wrapperMinHeights.text}
            ctaMinHeight={wrapperMinHeights.cta}
            hasGradientBackground={hasGradientBackground}
            hasMidBlueBackground={hasMidBlueBackground}
            index={index}
            isLast={gridItems?.length === index + 1}
            {...contentBlockProps}
          />
        </div>
      )}
    />
  );
};
